// Saved outlets — device-local favourites and recently viewed outlets.
//
// Browsing the mall needs no sign-in, so both lists live in localStorage
// (per device/browser). Only the outlet code + a display name are stored;
// live availability (online / item count / location) is merged from the
// /api/mall/outlets poll when rendering.

export interface SavedOutlet {
  code: string;
  name: string;
}

export interface RecentOutlet extends SavedOutlet {
  /** Epoch ms of the last visit — the list is kept newest-first. */
  viewedAt: number;
}

const FAVOURITES_KEY = 'ppos_mall_fav_outlets';
const RECENT_KEY = 'ppos_mall_recent_outlets';
const MAX_RECENT = 12;
/** Fired after any write so open pages re-render their saved lists. */
export const SAVED_OUTLETS_CHANGED_EVENT = 'mall-saved-outlets-changed';

function readList<T extends SavedOutlet>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is T => !!x && typeof x === 'object' && typeof x.code === 'string' && x.code.trim() !== '',
    );
  } catch {
    return [];
  }
}

function writeList<T>(key: string, list: T[]) {
  try {
    if (list.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(SAVED_OUTLETS_CHANGED_EVENT));
  } catch {
    /* storage disabled — the lists just won't persist */
  }
}

function sameOutlet(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function cleanOutlet(outlet: SavedOutlet): SavedOutlet {
  return { code: outlet.code.trim(), name: (outlet.name || outlet.code).trim() };
}

export function loadFavouriteOutlets(): SavedOutlet[] {
  return readList<SavedOutlet>(FAVOURITES_KEY).map(cleanOutlet);
}

export function isFavouriteOutlet(code: string): boolean {
  return !!code && readList<SavedOutlet>(FAVOURITES_KEY).some((o) => sameOutlet(o.code, code));
}

/** Add or remove an outlet. Returns true when it is a favourite afterwards. */
export function toggleFavouriteOutlet(outlet: SavedOutlet): boolean {
  const entry = cleanOutlet(outlet);
  if (!entry.code) return false;
  const list = readList<SavedOutlet>(FAVOURITES_KEY).map(cleanOutlet);
  const index = list.findIndex((o) => sameOutlet(o.code, entry.code));
  if (index >= 0) {
    list.splice(index, 1);
    writeList(FAVOURITES_KEY, list);
    return false;
  }
  writeList(FAVOURITES_KEY, [entry, ...list]);
  return true;
}

/** Recently viewed outlets, newest first. */
export function loadRecentOutlets(): RecentOutlet[] {
  return readList<RecentOutlet>(RECENT_KEY)
    .filter((o) => typeof o.viewedAt === 'number')
    .sort((a, b) => b.viewedAt - a.viewedAt);
}

/** Record an outlet visit — moves it to the top of "Recently viewed". */
export function recordOutletView(outlet: SavedOutlet) {
  const entry = cleanOutlet(outlet);
  if (!entry.code) return;
  const list = readList<RecentOutlet>(RECENT_KEY);
  const top = list[0];
  // Already the most recent entry with the same display name — nothing to do
  // (avoids a redundant localStorage write + re-render on every route pass).
  if (top && sameOutlet(top.code, entry.code) && top.name === entry.name) return;
  const rest = list.filter((o) => !sameOutlet(o.code, entry.code));
  writeList(RECENT_KEY, [{ ...entry, viewedAt: Date.now() }, ...rest].slice(0, MAX_RECENT));
}

export function clearRecentOutlets() {
  writeList(RECENT_KEY, []);
}

function renameAll<T extends SavedOutlet>(list: T[], names: Map<string, string>): T[] | null {
  let changed = false;
  const next = list.map((entry) => {
    const name = names.get(entry.code.toLowerCase());
    if (name && name !== entry.name) {
      changed = true;
      return { ...entry, name } as T;
    }
    return entry;
  });
  return changed ? next : null;
}

/** Update stored display names from the live outlet list (no reordering).
 *  A deep link to /s/<code> can record the outlet before the availability
 *  poll has returned its name, leaving the code as the placeholder. */
export function refreshSavedOutletNames(outlets: SavedOutlet[]) {
  const names = new Map<string, string>();
  for (const o of outlets) {
    const name = (o.name || '').trim();
    if (o.code && name) names.set(o.code.toLowerCase(), name);
  }
  if (names.size === 0) return;
  const favourites = renameAll(readList<SavedOutlet>(FAVOURITES_KEY).map(cleanOutlet), names);
  const recents = renameAll(readList<RecentOutlet>(RECENT_KEY), names);
  if (favourites) writeList(FAVOURITES_KEY, favourites);
  if (recents) writeList(RECENT_KEY, recents);
}
